import React, { MouseEvent, useEffect, useRef, useState } from 'react';
import { ArrowDropDown as ArrowDropDownIcon, ArrowDropUp as ArrowDropUpIcon } from '@mui/icons-material';
import { Box, Button, ClickAwayListener, MenuItem, MenuList, Paper, Popper, styled } from '@mui/material';
import clsx from 'clsx';
import { useParams } from 'react-router-dom';
import { NavItems } from 'common/types/store';
import { NavigationParams } from 'components/HorizontalNavigation/types';
import { stopEventPropagation } from 'utilities';
import { TabSubMenuProps } from './types';

export const HORIZONTAL_NAVIGATION_Z_INDEX = 1000;
export const HORIZONTAL_NAVIGATION_SELECTED_COLOR = '#80A4C5';

const StyledBoxContainer = styled(Box)(({ theme }) => ({
  minHeight: theme.spacing(6),
  zIndex: HORIZONTAL_NAVIGATION_Z_INDEX,
}));

const StyledButtonNavItem = styled(Button, {
  shouldForwardProp: prop => prop !== 'isMenuSelected',
})<{ isMenuSelected?: boolean }>(({ theme, isMenuSelected }) => ({
  borderRadius: 0,
  color: isMenuSelected ? theme.palette.common.white : theme.palette.primary[200],
  opacity: isMenuSelected ? 1 : 0.7,
  paddingBottom: theme.spacing(isMenuSelected ? 0.75 : 1),
  fontSize: theme.spacing(1.65),
  fontWeight: 500,
  minWidth: 0,
  padding: `${theme.spacing(1.25)} ${theme.spacing(2.5)}`,
  paddingRight: 0,
  '&.selected': {
    color: theme.palette.common.white,
  },
  '& span': {
    height: '100%',
  },
  '&:hover': {
    color: theme.palette.common.white,
  },
}));

const StyledArrowDropUpIcon = styled(ArrowDropUpIcon)(({ theme }) => ({
  color: HORIZONTAL_NAVIGATION_SELECTED_COLOR,
  fontWeight: 500,
  height: '100%',
  marginLeft: `${theme.spacing(0.5)}px`,
  padding: `0px ${theme.spacing(0.44)}px`,
  '.selected &': {
    color: theme.palette.common.white,
  },
}));

const StyledArrowDropDownIcon = styled(ArrowDropDownIcon)(({ theme }) => ({
  color: HORIZONTAL_NAVIGATION_SELECTED_COLOR,
  fontWeight: 500,
  height: '100%',
  marginLeft: `${theme.spacing(0.5)}px`,
  padding: `0px ${theme.spacing(0.44)}px`,
  '.selected &': {
    color: theme.palette.common.white,
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  backgroundColor: theme.palette.primary[600],
  color: theme.palette.primary[200],
  marginTop: theme.spacing(0.2),
  padding: `${theme.spacing(1.7)} ${theme.spacing(3)}`,
  textTransform: 'uppercase',
  fontSize: theme.spacing(1.75),
  '&:hover': {
    backgroundColor: theme.palette.primary[600],
    color: theme.palette.common.white,
  },
  '&:focus': {
    backgroundColor: theme.palette.primary[600],
  },
}));

const TabSubMenu = (props: TabSubMenuProps) => {
  const { item, onChange, disabled } = props;
  const { id, itemKey, slug, subMenu, title } = item;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | SVGSVGElement | null>(null);
  const [subMenuConfig, setSubMenuConfig] = useState({ width: 0, top: 0, left: 0 });

  const { tableSlugParam, tabSlugParam } = useParams<NavigationParams>();
  const slugParam = tableSlugParam ?? tabSlugParam;
  const isMenuSelected = slugParam && slug === slugParam;

  const tabMenuRef = useRef<HTMLButtonElement | null>(null);

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (tabMenuRef.current) {
      const element = tabMenuRef?.current;
      const position = element?.getBoundingClientRect();

      setSubMenuConfig({ width: position.width, top: position.bottom, left: position.left });
    }
  }, [tabMenuRef]);

  const openMenu = (event: MouseEvent<HTMLElement | SVGSVGElement>) => {
    setAnchorEl(event?.currentTarget);
  };
  const closeMenu = () => setAnchorEl(null);

  const handleOnClickSubMenu = (event: MouseEvent<HTMLLIElement>, key: NavItems['itemKey']) => {
    stopEventPropagation(event);
    setAnchorEl(null);
    onChange(event, key);
  };

  return (
    <StyledBoxContainer onMouseEnter={openMenu} onMouseLeave={closeMenu}>
      <StyledButtonNavItem
        className={clsx({ selected: isMenuSelected })}
        id={`tab-${id}`}
        onClick={event => onChange(event, itemKey)}
        ref={tabMenuRef}
        type="button">
        {title}

        {/* Icon */}
        {open ? (
          <StyledArrowDropUpIcon
            aria-controls={`menu-${id}`}
            aria-expanded={open}
            aria-haspopup="true"
            className="icon"
            onClick={openMenu}
          />
        ) : (
          <StyledArrowDropDownIcon
            aria-controls={`menu-${id}`}
            aria-expanded={open}
            aria-haspopup="true"
            className="icon"
            onClick={openMenu}
          />
        )}
      </StyledButtonNavItem>

      {/* Sub-Menu */}
      <Popper
        anchorEl={tabMenuRef.current}
        open={open}
        placement="bottom-start"
        role={undefined}
        // transition
        sx={{
          zIndex: 1000,
        }}>
        <Paper
          sx={{
            backgroundColor: 'transparent',
            borderRadius: 0,
            boxShadow: 'none',
          }}>
          <ClickAwayListener onClickAway={closeMenu}>
            <MenuList
              aria-labelledby={`tab-${id}`}
              autoFocusItem={open}
              id={`menu-${id}`}
              sx={{
                paddingTop: 0,
              }}>
              {subMenu?.map(subMenuItem => (
                <StyledMenuItem
                  key={subMenuItem?.itemKey}
                  onClick={event => handleOnClickSubMenu(event, subMenuItem?.itemKey)}
                  style={{ minWidth: subMenuConfig.width }}
                  disabled={disabled}>
                  {subMenuItem?.title}
                </StyledMenuItem>
              ))}
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Popper>
    </StyledBoxContainer>
  );
};

export default TabSubMenu;
